import { useI18n } from '../../i18n';
// ============================================
// RolesPage - Manage Roles and Permissions
// ============================================

import { useState, useMemo, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { PageHeader, PageContainer, Pagination } from '../../components/shared';
import { ActionButton, showToast } from '../../components/shared';

// ============================================
// Types
// ============================================

type RoleStatus = 'active' | 'inactive';

interface RoleDisplay {
  id: string;
  code: string;
  positionName: string;
  displayName: string;
  parentPosition: string;
  sort: number;
  status: RoleStatus;
}

interface RoleFormData {
  code: string;
  positionName: string;
  displayName: string;
  parentPosition: string;
  sort: string;
  status: RoleStatus;
}

type ModalMode = 'add' | 'edit' | null;

const emptyForm: RoleFormData = {
  code: '',
  positionName: '',
  displayName: '',
  parentPosition: '',
  sort: '',
  status: 'active',
};

// ============================================
// Mock data
// ============================================

const MOCK_ROLES: RoleDisplay[] = [
  { id: '1', code: 'POS001', positionName: 'Super Admin',    displayName: 'Super Admin',    parentPosition: '-',           sort: 1, status: 'active' },
  { id: '2', code: 'POS001', positionName: 'Admin',          displayName: 'Admin',          parentPosition: 'Super Admin', sort: 2, status: 'active' },
  { id: '3', code: 'POS001', positionName: 'Manager',        displayName: 'Manager',        parentPosition: 'Admin',       sort: 3, status: 'active' },
  { id: '4', code: 'POS001', positionName: 'Supervisor',     displayName: 'Supervisor',     parentPosition: 'Manager',     sort: 4, status: 'active' },
  { id: '5', code: 'POS001', positionName: 'Staff',          displayName: 'Staff',          parentPosition: 'Supervisor',  sort: 5, status: 'inactive' },
  { id: '6', code: 'POS001', positionName: 'Warehouse Staff',displayName: 'Warehouse Staff',parentPosition: 'Staff',       sort: 6, status: 'active' },
  { id: '7', code: 'POS001', positionName: 'Intern',         displayName: 'Intern',         parentPosition: 'Staff',       sort: 7, status: 'active' },
  { id: '8', code: 'POS002', positionName: 'Cashier',        displayName: 'Cashier',        parentPosition: 'Staff',       sort: 8, status: 'active' },
  { id: '9', code: 'POS002', positionName: 'Logistics',      displayName: 'Logistics',      parentPosition: 'Manager',     sort: 9, status: 'inactive' },
  { id: '10',code: 'POS002', positionName: 'Sales Rep',      displayName: 'Sales Rep',      parentPosition: 'Supervisor',  sort: 10,status: 'active' },
  { id: '1', code: 'POS001', positionName: 'Super Admin',    displayName: 'Super Admin',    parentPosition: '-',           sort: 1, status: 'active' },
  { id: '2', code: 'POS001', positionName: 'Admin',          displayName: 'Admin',          parentPosition: 'Super Admin', sort: 2, status: 'active' },
  { id: '3', code: 'POS001', positionName: 'Manager',        displayName: 'Manager',        parentPosition: 'Admin',       sort: 3, status: 'active' },
  { id: '4', code: 'POS001', positionName: 'Supervisor',     displayName: 'Supervisor',     parentPosition: 'Manager',     sort: 4, status: 'active' },
  { id: '5', code: 'POS001', positionName: 'Staff',          displayName: 'Staff',          parentPosition: 'Supervisor',  sort: 5, status: 'inactive' },
  { id: '6', code: 'POS001', positionName: 'Warehouse Staff',displayName: 'Warehouse Staff',parentPosition: 'Staff',       sort: 6, status: 'active' },
  { id: '7', code: 'POS001', positionName: 'Intern',         displayName: 'Intern',         parentPosition: 'Staff',       sort: 7, status: 'active' },
  { id: '8', code: 'POS002', positionName: 'Cashier',        displayName: 'Cashier',        parentPosition: 'Staff',       sort: 8, status: 'active' },
  { id: '9', code: 'POS002', positionName: 'Logistics',      displayName: 'Logistics',      parentPosition: 'Manager',     sort: 9, status: 'inactive' },
  
];

// ============================================
// Icons
// ============================================

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function EditIcon() {
  const { t } = useI18n();
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  const { t } = useI18n();
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ChevronIcon() {
  const { t } = useI18n();
  return (
    <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ============================================
// Status Badge
// ============================================

function StatusBadge({ status }: { status: RoleStatus }) {
  const { t } = useI18n();
  if (status === 'active') {
    return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">{t('rolesActive')}</span>;
  }
  return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-600">{t('rolesInactive')}</span>;
}

// ============================================
// Main Component
// ============================================

export function RolesPage() {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RoleFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof RoleFormData, string>>>({});
  const [roles, setRoles] = useState<RoleDisplay[]>(MOCK_ROLES);
  const pageSize = 10;

  const filtered = useMemo(() => {
    return roles.filter((r) => {
      const matchSearch = !debouncedSearch
        || r.code.toLowerCase().includes(debouncedSearch.toLowerCase())
        || r.positionName.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchStatus = !statusFilter || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [roles, debouncedSearch, statusFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  const openAdd = useCallback(() => { setFormData(emptyForm); setFormErrors({});  setActiveRoleId(null); setModalMode('add'); }, []);
  const openEdit = useCallback((r: RoleDisplay) => {
    setFormData({ code: r.code, positionName: r.positionName, displayName: r.displayName, parentPosition: r.parentPosition === '-' ? '' : r.parentPosition, sort: String(r.sort), status: r.status });
    setFormErrors({});
    
    setActiveRoleId(r.id);
    setModalMode('edit');
  }, []);
  const closeModal = useCallback(() => { setModalMode(null); setActiveRoleId(null);  setFormErrors({}); }, []);
  const handleField = (key: keyof RoleFormData, value: string) => setFormData((p) => ({ ...p, [key]: value }));

  const handleSave = useCallback(() => {
    const errors: Partial<Record<keyof RoleFormData, string>> = {};
    if (!formData.positionName.trim()) errors.positionName = t('validationPositionNameRequired');
    if (!formData.displayName.trim()) errors.displayName = t('validationDisplayNameRequired');
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    
    if (modalMode === 'add') {
      const newRole: RoleDisplay = {
        id: String(Date.now()),
        code: formData.code.trim() || `POS${String(roles.length + 1).padStart(3, '0')}`,
        positionName: formData.positionName.trim(),
        displayName: formData.displayName.trim(),
        parentPosition: formData.parentPosition.trim() || '-',
        sort: Number(formData.sort) || roles.length + 1,
        status: formData.status,
      };
      setRoles((prev) => [...prev, newRole]);
    } else if (modalMode === 'edit' && activeRoleId) {
      setRoles((prev) => prev.map((r) =>
        r.id === activeRoleId
          ? { ...r, code: formData.code.trim() || r.code, positionName: formData.positionName.trim(), displayName: formData.displayName.trim(), parentPosition: formData.parentPosition.trim() || '-', sort: Number(formData.sort) || r.sort, status: formData.status }
          : r
      ));
    }
    closeModal();
  }, [formData, modalMode, activeRoleId, roles.length, closeModal, t]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmId) return;
    setRoles((prev) => prev.filter((r) => r.id !== deleteConfirmId));
    setDeleteConfirmId(null);
  }, [deleteConfirmId]);

  const inputCls = "h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none";

  return (
    <PageContainer>
      <PageHeader
        title={t('roles')}
        subtitle={t('manageRolesAndPermissions')}
        actions={
          <ActionButton variant="primary" icon={<PlusIcon />} onClick={openAdd}>
            Add Role
          </ActionButton>
        }
      />

      {/* Search & Filter bar */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-6 py-4 flex items-center gap-3">
        <div className="relative w-[260px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder={t('searchByCodeOrName')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            enterKeyHint="search"
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
          />
        </div>
        <div className="ml-auto relative">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="h-9 w-[160px] appearance-none rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-sm text-slate-600 focus:outline-none"
          >
            <option value="">{t('allStatus')}</option>
            <option value="active">{t('rolesActive')}</option>
            <option value="inactive">{t('rolesInactive')}</option>
          </select>
          <ChevronIcon />
        </div>
      </div>

      {/* Table card */}
      <div className="rounded-xl bg-white" style={{ marginTop: '15px', boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#E5E7EB' }}>
                {['CODE', 'POSITION NAME', 'DISPLAY NAME', 'PARENT POSITION', 'SORT', 'STATUS', 'ACTIONS'].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">{t('noRolesFound')}</td></tr>
              ) : (
                paginated.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-700">{r.code}</td>
                    <td className="px-4 py-3 text-sm text-slate-800">{r.positionName}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{r.displayName}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{r.parentPosition}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{r.sort}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(r)} className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"><EditIcon /></button>
                        <button onClick={() => setDeleteConfirmId(r.id)} className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          variant="numbered"
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filtered.length}
          pageSize={pageSize}
          onPageChange={(p) => setCurrentPage(p)}
          itemLabel="roles"
        />
      </div>

      {/* Add/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={closeModal}>
          <div className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">{modalMode === 'add' ? 'Add Role' : 'Edit Role'}</h3>
              <button onClick={closeModal} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><XIcon /></button>
            </div>
            <div className="space-y-4 px-6 pb-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('code')}</label>
                <input value={formData.code} onChange={(e) => handleField('code', e.target.value)} placeholder={t('enterCode')} className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('positionName')} <span className="text-red-500">*</span></label>
                <input value={formData.positionName} onChange={(e) => handleField('positionName', e.target.value)} placeholder={t('enterPositionName')} className={`${inputCls} ${formErrors.positionName ? 'border-red-300 bg-red-50' : ''}`} />
                {formErrors.positionName && <p className="mt-1 text-xs text-red-600">{formErrors.positionName}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('displayName')} <span className="text-red-500">*</span></label>
                <input value={formData.displayName} onChange={(e) => handleField('displayName', e.target.value)} placeholder={t('enterDisplayName')} className={`${inputCls} ${formErrors.displayName ? 'border-red-300 bg-red-50' : ''}`} />
                {formErrors.displayName && <p className="mt-1 text-xs text-red-600">{formErrors.displayName}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('parentPosition')}</label>
                  <input value={formData.parentPosition} onChange={(e) => handleField('parentPosition', e.target.value)} placeholder={t('enterPositionRole')} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('sort')}</label>
                  <input type="number" value={formData.sort} onChange={(e) => handleField('sort', e.target.value)} placeholder={t('enterSort')} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('rolesStatus')}</label>
                <div className="relative">
                  <select value={formData.status} onChange={(e) => handleField('status', e.target.value)} className="h-10 w-full appearance-none rounded-lg border border-slate-300 px-3 pr-8 text-sm text-slate-700 focus:border-blue-400 focus:outline-none">
                    <option value="active">{t('rolesActive')}</option>
                    <option value="inactive">{t('rolesInactive')}</option>
                  </select>
                  <ChevronIcon />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
              <button onClick={closeModal} className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">{t('rolesCancel')}</button>
              <button onClick={handleSave} className="rounded-lg bg-[#0288D1] px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">{t('rolesSave')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setDeleteConfirmId(null)}>
          <div className="w-full max-w-[440px] rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">{t('deleteRole')}</h3>
              <button onClick={() => setDeleteConfirmId(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><XIcon /></button>
            </div>
            <div className="border-t border-slate-100" />
            <div className="px-6 py-5">
              <p className="text-sm text-slate-500">Are you sure you want to delete this position?</p>
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <button onClick={() => setDeleteConfirmId(null)} className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">{t('rolesCancel')}</button>
              <button onClick={handleConfirmDelete} className="rounded-lg bg-[#F44336] px-5 py-2 text-sm font-semibold text-white hover:bg-red-600 transition-colors">{t('rolesDelete')}</button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
